import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, MessageCircle, ThumbsUp, Repeat2, MessageSquare, Award, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import SectionNav from '../../../components/layout/SectionNav';
import { discoverTabs } from '../../../nav/publicNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

const TIER_COLORS = { A: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', B: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', C: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' };
const TIER_LABELS = { A: 'Gold', B: 'Silver', C: 'Bronze' };

export default function SentimentDashboardPage() {
  const [trending, setTrending] = useState([]);
  const [voices, setVoices] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState('trending');

  const fetchTrending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [trendRes, voicesRes] = await Promise.all([
        fetch(`${API}/sentiment/trending?days=${days}&limit=20`),
        fetch(`${API}/sentiment/voices?days=${days}&limit=15`),
      ]);
      const [tData, vData] = await Promise.all([trendRes.json(), voicesRes.json()]);
      setTrending(tData.stocks || []);
      setVoices(vData.voices || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [days]);

  const fetchStockPosts = async (isin, symbol) => {
    setSelectedStock({ isin, symbol });
    setPostsLoading(true);
    try {
      const res = await fetch(`${API}/sentiment/stock/${isin}?days=${days}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPosts(data.posts || []);
    } catch (e) { setError(e.message); }
    setPostsLoading(false);
  };

  useEffect(() => { fetchTrending(); }, [fetchTrending]);

  return (
    <div className="space-y-4">
      <SectionNav tabs={discoverTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-500" /> Social Sentiment
        </h1>
        <p className="text-xs text-muted-foreground">X/Twitter signal trends, most-discussed stocks, and top credible voices</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-0 border-b border-border">
          {[
            { key: 'trending', label: 'Trending Stocks' },
            { key: 'voices', label: 'Top Voices' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${tab === t.key ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 ml-auto">
          {[7, 14, 30].map(d => (
            <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'}
              className={`text-xs h-7 ${days === d ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              onClick={() => setDays(d)}>
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : (
        <>
          {trending.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {trending.map((s, i) => (
                <button key={i} onClick={() => fetchStockPosts(s.isin, s.symbol)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${selectedStock?.isin === s.isin ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-background border-border hover:bg-muted'}`}>
                  {s.symbol || s.company_name}
                  <span className="text-[10px] opacity-70">{s.post_count}</span>
                </button>
              ))}
            </div>
          )}

          {selectedStock && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold">{selectedStock.symbol} Posts</h2>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedStock(null); setPosts([]); }}>Clear</Button>
              </div>
              {postsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              ) : posts.length > 0 ? (
                <div className="space-y-3">
                  {posts.map((p, i) => (
                    <Card key={i}>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
                            {(p.author || '?')[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-semibold">@{p.author}</span>
                              {p.followers > 0 && <span className="text-[10px] text-muted-foreground">{(p.followers / 1000).toFixed(1)}k followers</span>}
                              {p.tier && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${TIER_COLORS[p.tier] || ''}`}>{TIER_LABELS[p.tier] || p.tier}</span>}
                            </div>
                            <p className="text-xs leading-relaxed mb-2">{p.text}</p>
                            <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
                              <span className="flex items-center gap-0.5"><ThumbsUp className="w-3 h-3" /> {p.likes}</span>
                              <span className="flex items-center gap-0.5"><Repeat2 className="w-3 h-3" /> {p.reposts}</span>
                              <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" /> {p.replies}</span>
                              {p.credibility && <span>Credibility: {p.credibility}</span>}
                              {p.created_at && <span className="ml-auto">{new Date(p.created_at).toLocaleDateString()}</span>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-muted-foreground">No posts found for this stock in the last {days} days.</div>
              )}
            </div>
          )}

          {tab === 'trending' && !selectedStock && (
            trending.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-emerald-800 text-white">
                      <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Stock</th>
                      <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Posts</th>
                      <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Authors</th>
                      <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Likes</th>
                      <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Credibility</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trending.map((s, i) => (
                      <tr key={i} className="border-b border-muted/20 hover:bg-muted/20 cursor-pointer"
                        onClick={() => fetchStockPosts(s.isin, s.symbol)}>
                        <td className="py-2 px-3 font-medium">{s.company_name} <span className="text-muted-foreground">{s.symbol}</span></td>
                        <td className="py-2 px-3 text-right font-semibold">{s.post_count}</td>
                        <td className="py-2 px-3 text-right">{s.unique_authors}</td>
                        <td className="py-2 px-3 text-right">{s.total_likes}</td>
                        <td className="py-2 px-3 text-right">{s.avg_credibility ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No sentiment data yet.</p>
                <p className="text-xs text-muted-foreground mt-1">Run the X posts bulk ingestion to populate data.</p>
              </div>
            )
          )}

          {tab === 'voices' && (
            voices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-emerald-800 text-white">
                      <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Author</th>
                      <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Followers</th>
                      <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Posts</th>
                      <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Stocks</th>
                      <th className="text-center py-2 px-3 font-semibold text-[10px] uppercase">Tier</th>
                      <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Credibility</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voices.map((v, i) => (
                      <tr key={i} className="border-b border-muted/20 hover:bg-muted/20">
                        <td className="py-2 px-3">
                          <span className="font-medium">@{v.handle}</span>
                          {v.name && <span className="text-muted-foreground ml-1">{v.name}</span>}
                        </td>
                        <td className="py-2 px-3 text-right">{v.followers > 0 ? `${(v.followers / 1000).toFixed(1)}k` : '—'}</td>
                        <td className="py-2 px-3 text-right">{v.post_count}</td>
                        <td className="py-2 px-3 text-right">{v.stocks_covered}</td>
                        <td className="py-2 px-3 text-center">
                          {v.tier && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${TIER_COLORS[v.tier] || ''}`}>{TIER_LABELS[v.tier] || v.tier}</span>}
                        </td>
                        <td className="py-2 px-3 text-right">{v.avg_credibility ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No voices data yet.</p>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
